import {FormHelperText, Grid} from '@mui/material';
import {FormikErrors, useFormikContext} from 'formik';
import {FormProps, OptionValue} from "../../model/form";
import {JsonEmailMessage, JsonFileInfo, JsonTemplate, JsonTemplateDataHeaderTypeEnum} from "../../generated-api";
import {useAppTranslation} from "../../services/i18n";
import FormContainer from "../form/FormContainer";
import {TextFormField} from "../form/TextFormField";
import {SimpleSelectFormField} from "../form/SimpleSelectFormField";
import {isTemplateWithVariants, templateCodeTitle, TEMPLATE_TYPE_INVITE} from "../../model/template";
import {useCallback, useEffect, useMemo, useState} from "react";
import {useAppDispatch} from "../../store";
import {previewTemplate} from "../../store/templates";
import {getApiResult} from "../../helpers/api";
import {fetchFileInfo} from "../../store/files";
import {EmailPreview} from "../EmailPreview";

interface Props extends FormProps<JsonTemplate> {
}

const headerTypeOptions: OptionValue[] = [
    {value: JsonTemplateDataHeaderTypeEnum.Org, label: 'Organizátoři'},
    {value: JsonTemplateDataHeaderTypeEnum.Guest, label: 'VIP hosté'},
];

const PREVIEW_DELAY = 300;

/** Renders the edited template as the real e-mail, refreshed shortly after the form values settle. */
const TemplatePreview = () => {
    const dispatch = useAppDispatch();
    const {values} = useFormikContext<JsonTemplate>();
    const [preview, setPreview] = useState<JsonEmailMessage>();
    const [attachInfos, setAttachInfos] = useState<JsonFileInfo[]>([]);

    const attachKey = (values.templateData?.attachFileIds || []).join(',');
    useEffect(() => {
        const fileIds = attachKey ? attachKey.split(',').map((s) => parseInt(s, 10)) : [];
        Promise.all(fileIds.map((fileId) => dispatch(fetchFileInfo(fileId))))
            .then((results) => setAttachInfos(results
                .map((res) => getApiResult<JsonFileInfo>(res))
                .filter((info): info is JsonFileInfo => !!info)));
    }, [attachKey, dispatch]);

    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(() => {
            dispatch(previewTemplate(values)).then((res) => {
                if (!cancelled) {
                    setPreview(getApiResult<JsonEmailMessage>(res));
                }
            });
        }, PREVIEW_DELAY);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [values, dispatch]);

    // attachments the e-mail does not carry itself are still part of it, behind the link
    return <EmailPreview email={preview} bodyOnly={values.templateType === TEMPLATE_TYPE_INVITE} linkedFileInfos={preview && !preview.attachFileInfos?.length ? attachInfos : undefined}/>;
}

const EventTemplateForm = (props: Props) => {

    const t = useAppTranslation();
    const {item} = props;

    const isInvite = item.templateType === TEMPLATE_TYPE_INVITE;
    const isVariants = isTemplateWithVariants(item.templateCode);

    const values = useMemo((): JsonTemplate => ({
        ...item,
        templateData: {
            ...item.templateData,
            headerType: isInvite ? undefined : (item.templateData?.headerType || JsonTemplateDataHeaderTypeEnum.Org)
        },
    }), [item, isInvite]);

    const handleValidate = useCallback((values: JsonTemplate) => {
        const errors = {} as FormikErrors<JsonTemplate>;
        if (!isInvite && !values.subject) {
            errors.subject = t('Zadejte prosím předmět');
        }
        if (!values.bodyTop) {
            errors.bodyTop = t('Zadejte prosím text');
        }
        return errors;
    }, [isInvite, t]);

    return (
        <FormContainer {...props} item={values} validate={handleValidate} children={<>
            <Grid item lg={6} md={6}>
                <TemplatePreview/>
            </Grid>
            <Grid item lg={6} md={6}>
                <Grid container columnSpacing={2} rowSpacing={1}>
                    <Grid item xs={12}>
                        <strong>{templateCodeTitle(item.templateCode)}</strong>
                        {' '}<span style={{color: 'var(--gray-text)'}}>
                            {item.templateCode}{isInvite ? ' – ' + t('text se nezasílá, doplní se do stránky pozvánky') : ''}
                        </span>
                    </Grid>
                    {!isInvite && <Grid item xs={4}>
                        <SimpleSelectFormField name="templateData.headerType" label={t('Záhlaví')} options={headerTypeOptions}/>
                    </Grid>}
                    {isVariants && <Grid item xs={8}>
                        <TextFormField name="templateData.title" label={t('Varianta')} type={'text'} maxlength={100}/>
                    </Grid>}
                    {!isInvite && <Grid item xs={12}>
                        <TextFormField name="subject" label={t('Předmět e-mailu')} type={'text'} maxlength={255}/>
                    </Grid>}
                    <Grid item xs={12}>
                        <TextFormField name="bodyTop" label={t('Text nad obsahem')} type={'textarea'} minRows={12} maxRows={18} maxlength={100_000}/>
                    </Grid>
                    <Grid item xs={12}>
                        <TextFormField name="bodyBottom" label={t('Text pod obsahem')} type={'textarea'} minRows={4} maxRows={8} maxlength={100_000}/>
                    </Grid>
                    {item.templateCode === 'SAFETY_ORG' &&<Grid item xs={12}>
                        <TextFormField name="templateData.confirmText" label={t('Text potvrzení (zaškrtávací pole)')} type={'textarea'} minRows={2} maxRows={4} maxlength={1000}/>
                        <FormHelperText>{t('Zobrazí se u výzvy BOZP i v pozvánce organizátora. Prázdné = výchozí text.')}</FormHelperText>
                    </Grid>}
                    {!isInvite && <Grid item xs={12}>
                        <FormHelperText>{t('Záhlaví a přílohy se nahrávají centrálně na záložce E-maily, platí pro všechny e-maily, které je používají.')}</FormHelperText>
                    </Grid>}
                </Grid>
            </Grid>
        </>}/>
    );
}

export default EventTemplateForm;
